import axios from 'axios';
import { API_BASE_URL } from './config';

// Create axios instance with base configuration
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Invoice interface
export interface Invoice {
  id: string;
  invoiceNumber: string;
  client: {
    name: string;
    company: string;
    email: string;
  };
  project: string;
  amount: number;
  taxAmount: number;
  totalAmount: number;
  status: 'draft' | 'sent' | 'viewed' | 'partial' | 'paid' | 'overdue' | 'cancelled';
  dueDate: string;
  issueDate: string;
  paidDate?: string;
  paidAmount?: number;
  description: string;
  items: {
    description: string;
    quantity: number;
    rate: number;
    amount: number;
  }[];
  paymentTerms: string;
  notes?: string;
  createdBy: string;
  lastSent?: string;
  projectId?: string;
  clientId?: string;
  createdAt?: string;
  updatedAt?: string;
}

// Invoice filters interface
export interface InvoiceFilters {
  project?: string;
  client?: string;
  status?: string;
  search?: string;
  dateFrom?: string;
  dateTo?: string;
  dueDateFrom?: string;
  dueDateTo?: string;
  amountMin?: number;
  amountMax?: number;
}

// Invoice statistics interface
export interface InvoiceStats {
  totalInvoiced: number;
  totalPaid: number;
  totalOutstanding: number;
  totalOverdue: number;
  averageInvoiceAmount: number;
  averagePaymentTime: number;
  draftCount: number;
  sentCount: number;
  paidCount: number;
  overdueCount: number;
  partialCount: number;
  cancelledCount: number;
  monthlyStats: {
    month: string;
    invoiced: number;
    paid: number;
    outstanding: number;
  }[];
}

// Invoice creation/update interface
export interface InvoiceFormData {
  invoiceNumber?: string;
  client: {
    name: string;
    company: string;
    email: string;
  };
  project: string;
  amount: number;
  taxAmount?: number;
  description: string;
  items: {
    description: string;
    quantity: number;
    rate: number;
    amount: number;
  }[];
  paymentTerms: string;
  dueDate: string;
  issueDate: string;
  notes?: string;
  projectId?: string;
  clientId?: string;
  companyId?: string;
  company?: string;
}

// Payment record interface
export interface PaymentRecord {
  id: string;
  invoiceId: string;
  amount: number;
  paymentDate: string;
  paymentMethod: string;
  reference?: string;
  notes?: string;
  createdBy: string;
  createdAt: string;
}

// Invoice template interface
export interface InvoiceTemplate {
  id: string;
  name: string;
  description: string;
  items: {
    description: string;
    quantity: number;
    rate: number;
  }[];
  paymentTerms: string;
  notes?: string;
  isDefault: boolean;
}

class InvoicesService {
  // Normalize backend invoice object into the UI-friendly Invoice shape
  private normalizeInvoice(item: any): Invoice {
    const clientObj = item?.client || {};
    const projectObj = item?.project || {};
    const lineItems = item?.lineItems || item?.items || [];

    const clientName = (clientObj?.name ?? clientObj?.contactPerson?.name ?? '').toString();
    const clientCompany = (clientObj?.company ?? '').toString();
    const clientEmail = (
      clientObj?.email ?? clientObj?.contactPerson?.email ?? (Array.isArray(clientObj?.emails) ? clientObj.emails[0] : '')
    )?.toString() || '';

    const statusRaw = (item?.status ?? 'draft').toString();
    const statusLower = statusRaw.toLowerCase() as Invoice['status'];

    const taxAmount = Number(item?.taxAmount ?? item?.totalTax ?? item?.tax ?? 0);
    const baseAmount = Number(item?.amount ?? item?.subtotal ?? 0);
    const totalAmount = Number(item?.totalAmount ?? baseAmount + taxAmount);

    const issuedDate = item?.dates?.issued ?? item?.issueDate ?? null;
    const dueDate = item?.dates?.due ?? item?.dueDate ?? null;

    const paidAmount = Number(item?.payments?.totalPaid ?? item?.paidAmount ?? 0);
    const lastSent = item?.delivery?.lastSent ?? item?.lastSent ?? null;

    return {
      id: (item?._id ?? item?.id ?? '').toString(),
      invoiceNumber: (item?.invoiceNumber ?? item?.number ?? item?.reference ?? '').toString(),
      client: {
        name: clientName,
        company: clientCompany,
        email: clientEmail,
      },
      project: (projectObj?.name ?? item?.project ?? '').toString(),
      amount: baseAmount,
      taxAmount,
      totalAmount,
      status: statusLower,
      dueDate: dueDate ? new Date(dueDate).toISOString() : '',
      issueDate: issuedDate ? new Date(issuedDate).toISOString() : '',
      paidDate: item?.payments?.lastPaymentDate ? new Date(item.payments.lastPaymentDate).toISOString() : undefined,
      paidAmount,
      description: (item?.description ?? item?.summary ?? '').toString(),
      items: Array.isArray(lineItems)
        ? lineItems.map((li: any) => {
            const qty = Number(li?.quantity ?? 0);
            const rate = Number(li?.unitPrice ?? li?.rate ?? 0);
            const amount = Number(li?.totalPrice ?? li?.amount ?? qty * rate);
            return {
              description: (li?.description ?? li?.name ?? '').toString(),
              quantity: qty,
              rate,
              amount,
            };
          })
        : [],
      paymentTerms: (item?.terms?.paymentTerms ?? item?.paymentTerms ?? '').toString(),
      notes: (item?.notes?.internal ?? item?.notes?.client ?? item?.notes ?? '').toString() || undefined,
      createdBy: (item?.createdBy?._id ?? item?.createdBy?.id ?? item?.createdBy ?? '').toString(),
      lastSent: lastSent ? new Date(lastSent).toISOString() : undefined,
      projectId: (projectObj?._id ?? item?.projectId ?? '').toString() || undefined,
      clientId: (clientObj?._id ?? item?.clientId ?? '').toString() || undefined,
      createdAt: item?.createdAt ? new Date(item.createdAt).toISOString() : undefined,
      updatedAt: item?.updatedAt ? new Date(item.updatedAt).toISOString() : undefined,
    };
  }

  // Get all invoices
  async getInvoices(filters?: InvoiceFilters): Promise<Invoice[]> {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, value.toString());
        }
      });
    }
    const query = params.toString();
    const response = await api.get(`/invoices${query ? `?${query}` : ''}`);
    const data = response.data;
    // Backend may return an object { invoices, totalPages, currentPage, total }
    // Normalize to array and map to UI-friendly shape
    const rawList = Array.isArray(data) ? data : (data?.invoices ?? []);
    return rawList.map((item: any) => this.normalizeInvoice(item));
  }

  // Get invoice by ID
  async getInvoiceById(id: string): Promise<Invoice> {
    const response = await api.get(`/invoices/${id}`);
    return this.normalizeInvoice(response.data);
  }

  // Create new invoice
  async createInvoice(invoiceData: InvoiceFormData): Promise<Invoice> {
    // Include issuer company from current user profile when available
    const payload: any = { ...invoiceData };
    try {
      const me = await api.get('/auth/me');
      const user = me?.data?.user || me?.data;
      const company = user?.company;
      const companyId = typeof company === 'object' ? (company?._id || company?.id) : company;
      if (!payload.companyId && companyId) {
        payload.companyId = String(companyId);
      } else if (!payload.companyId && typeof company === 'object' && company?.name) {
        // As a secondary option, include company name if available
        payload.company = String(company.name);
      }
    } catch (e) {
      // Non-blocking: if company cannot be determined here, backend will validate
      // and respond with a clear error; caller will surface message.
    }

    const response = await api.post('/invoices', payload);
    return response.data;
  }

  // Update invoice
  async updateInvoice(id: string, invoiceData: Partial<InvoiceFormData>): Promise<Invoice> {
    const response = await api.put(`/invoices/${id}`, invoiceData);
    return response.data;
  }

  // Delete invoice
  async deleteInvoice(id: string): Promise<void> {
    await api.delete(`/invoices/${id}`);
  }

  // Get invoice statistics
  async getInvoiceStats(filters?: InvoiceFilters): Promise<InvoiceStats> {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, value.toString());
        }
      });
    }
    
    const response = await api.get(`/invoices/stats?${params.toString()}`);
    return response.data;
  }

  // Get invoices by project
  async getInvoicesByProject(projectId: string): Promise<Invoice[]> {
    const response = await api.get(`/invoices/project/${projectId}`);
    return response.data;
  }

  // Get invoices by client
  async getInvoicesByClient(clientId: string): Promise<Invoice[]> {
    const response = await api.get(`/invoices/client/${clientId}`);
    return response.data;
  }

  // Update invoice status
  async updateInvoiceStatus(id: string, status: Invoice['status']): Promise<Invoice> {
    const response = await api.patch(`/invoices/${id}/status`, { status });
    return response.data;
  }

  // Send invoice
  async sendInvoice(id: string, email?: string): Promise<Invoice> {
    const response = await api.post(`/invoices/${id}/send`, { email });
    return response.data;
  }

  // Record payment
  async recordPayment(invoiceId: string, paymentData: {
    amount: number;
    paymentDate: string;
    paymentMethod: string;
    reference?: string;
    notes?: string;
  }): Promise<PaymentRecord> {
    const response = await api.post(`/invoices/${invoiceId}/payments`, paymentData);
    return response.data;
  }

  // Get payment history
  async getPaymentHistory(invoiceId: string): Promise<PaymentRecord[]> {
    const response = await api.get(`/invoices/${invoiceId}/payments`);
    return response.data;
  }

  // Generate invoice PDF
  async generateInvoicePDF(id: string): Promise<Blob> {
    const response = await api.get(`/invoices/${id}/pdf`, {
      responseType: 'blob'
    });
    return response.data;
  }

  // Download invoice PDF
  async downloadInvoicePDF(id: string, filename?: string): Promise<void> {
    const blob = await this.generateInvoicePDF(id);
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename || `invoice-${id}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  // Print invoice
  async printInvoice(id: string): Promise<void> {
    const blob = await this.generateInvoicePDF(id);
    const url = window.URL.createObjectURL(blob);
    const printWindow = window.open(url);
    if (printWindow) {
      printWindow.onload = () => {
        printWindow.print();
      };
    }
  }

  // Duplicate invoice
  async duplicateInvoice(id: string): Promise<Invoice> {
    const response = await api.post(`/invoices/${id}/duplicate`);
    return response.data;
  }

  // Bulk update invoices
  async bulkUpdateInvoices(updates: { id: string; data: Partial<InvoiceFormData> }[]): Promise<Invoice[]> {
    const response = await api.patch('/invoices/bulk', { updates });
    return response.data;
  }

  // Bulk delete invoices
  async bulkDeleteInvoices(ids: string[]): Promise<void> {
    await api.delete('/invoices/bulk', { data: { ids } });
  }

  // Search invoices
  async searchInvoices(query: string, filters?: InvoiceFilters): Promise<Invoice[]> {
    const params = new URLSearchParams({ search: query });
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && key !== 'search') {
          params.append(key, value.toString());
        }
      });
    }
    
    const response = await api.get(`/invoices/search?${params.toString()}`);
    const data = response.data;
    const rawList = Array.isArray(data) ? data : (data?.invoices ?? []);
    return rawList.map((item: any) => this.normalizeInvoice(item));
  }

  // Export invoices
  async exportInvoices(format: 'csv' | 'excel' | 'pdf', filters?: InvoiceFilters): Promise<Blob> {
    const params = new URLSearchParams({ format });
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, value.toString());
        }
      });
    }
    
    const response = await api.get(`/invoices/export?${params.toString()}`, {
      responseType: 'blob'
    });
    return response.data;
  }

  // Import invoices
  async importInvoices(file: File): Promise<{ success: number; errors: string[] }> {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await api.post('/invoices/import', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  // Get overdue invoices
  async getOverdueInvoices(): Promise<Invoice[]> {
    const response = await api.get('/invoices/overdue');
    return response.data;
  }

  // Send payment reminder
  async sendPaymentReminder(id: string, message?: string): Promise<void> {
    await api.post(`/invoices/${id}/reminder`, { message });
  }

  // Get invoice templates
  async getInvoiceTemplates(): Promise<InvoiceTemplate[]> {
    const response = await api.get('/invoices/templates');
    return response.data;
  }

  // Create invoice template
  async createInvoiceTemplate(templateData: Omit<InvoiceTemplate, 'id'>): Promise<InvoiceTemplate> {
    const response = await api.post('/invoices/templates', templateData);
    return response.data;
  }

  // Update invoice template
  async updateInvoiceTemplate(id: string, templateData: Partial<InvoiceTemplate>): Promise<InvoiceTemplate> {
    const response = await api.put(`/invoices/templates/${id}`, templateData);
    return response.data;
  }

  // Delete invoice template
  async deleteInvoiceTemplate(id: string): Promise<void> {
    await api.delete(`/invoices/templates/${id}`);
  }

  // Create invoice from template
  async createInvoiceFromTemplate(templateId: string, invoiceData: Partial<InvoiceFormData>): Promise<Invoice> {
    const response = await api.post(`/invoices/templates/${templateId}/create`, invoiceData);
    return response.data;
  }

  // Get invoice aging report
  async getInvoiceAgingReport(): Promise<{
    current: number;
    days30: number;
    days60: number;
    days90: number;
    over90: number;
    total: number;
  }> {
    const response = await api.get('/invoices/aging-report');
    return response.data;
  }

  // Get revenue forecast
  async getRevenueForecast(months: number = 6): Promise<{
    month: string;
    projected: number;
    actual: number;
    variance: number;
  }[]> {
    const response = await api.get(`/invoices/revenue-forecast?months=${months}`);
    return response.data;
  }
}

const invoicesService = new InvoicesService();
export default invoicesService;